<script>
/* eslint-disable @typescript-eslint/no-use-before-define */
import { defineComponent, reactive, ref, toRef, watch, computed } from '@vue/composition-api'
import {
  CheckProjectServiceProxy,
  DeviceTypeCheckProjectServiceProxy,
  CheckItemServiceProxy,
} from '@/service-proxies/app-v2/tpm.service-proxies'
import useTable from '@/hooks/useTable'
import useModal from '@/hooks/useModal'
import i18n from '@/locales/index'

const cpsp = new CheckProjectServiceProxy()
const cisp = new CheckItemServiceProxy()
const dtcpsp = new DeviceTypeCheckProjectServiceProxy()

export default defineComponent({
  props: ['device'],
  setup(props, { root }) {
    const device = toRef(props, 'device')

    const addButtonProps = {
      class: 'mt-2',
      type: 'primary',
    }
    const addButtonEvents = {
      click() {
        addModalAttrs.visible = true
      },
    }

    const {
      tableAttrs: projectTableAttrs,
      read: getProjectTableData,
      del: deletePro,
      currentRecord: currentPro,
    } = useTable(
      {
        class: 'mt-2',
        columns: [
          { title: '操作', scopedSlots: { customRender: 'action' }, i18n: 'actions' },
          { title: '点检编码', dataIndex: 'code', i18n: 'page_tpm_check_project_code' },
          { title: '点检项目', dataIndex: 'name', i18n: 'page_tpm_check_project_name' },
          { title: '点检周期', dataIndex: 'intervalTime', i18n: 'page_tpm_check_project_interval' },
          { title: '备注', dataIndex: 'remark', i18n: 'page_tpm_check_project_remark' },
        ],
        rowSelection: null,
      },
      {
        read: dtcpsp.getDeviceTypeCheckProjectPage.bind(dtcpsp),
        delete: dtcpsp.deleteDeviceTypeCheckProject.bind(dtcpsp),
      },
      record =>
        getItemTableData(itemTableProps.pagination.current, record.checkProjectId, itemTableProps.pagination.pageSize),
      (page, pageSize) => getProjectTableData(page, props.device.id, pageSize),
      () => {
        if (itemTableProps) itemTableProps.dataSource = []
      }
    )

    const deleteProject = async record => {
      await deletePro(record.id)
      await getProjectTableData(
        projectTableAttrs.pagination.current,
        props.device.id,
        projectTableAttrs.pagination.pageSize
      )
      itemTableProps.dataSource = []
    }

    const { tableAttrs: itemTableProps, read: getItemTableData } = useTable(
      {
        class: 'mt-2',
        columns: [
          {
            title: '点检项',
            dataIndex: 'name',
            i18n: 'page_tpm_check_item_name',
          },
          {
            title: '数值类型',
            dataIndex: 'valueType',
            i18n: 'page_tpm_check_item_type',
          },
          {
            title: '点检要求',
            dataIndex: 'requirement',
            i18n: 'page_tpm_check_item_require',
          },
          {
            title: '标准数值',
            dataIndex: 'standardValue',
            i18n: 'page_tpm_check_item_standard',
          },
        ],
        rowSelection: null,
      },
      {
        read: cisp.pageCheckItems.bind(cisp),
      },
      undefined,
      (page, pageSize) => getItemTableData(page, currentPro.value.checkProjectId, pageSize)
    )

    const { modalAttrs: addModalAttrs, modalEvents: addModalEvents } = useModal(
      { title: root.$t('page_tpm_check_project_create') || '添加点检', okText: root.$t('page_calendar_add') || '添加' },
      {
        async ok() {
          try {
            addModalAttrs.confirmLoading = true
            const values = await validate()
            const body = {
              deviceTypeId: props.device.id,
              list: [
                {
                  checkProjectId: values.checkProjectId,
                  code: currentProject.value.code,
                  intervalTime: values.intervalTime,
                  name: currentProject.value.name,
                  remark: values.remark,
                },
              ],
            }
            await dtcpsp.createDeviceTypeCheckProjects(body)
            root.$message.success(root.$t('page_tpm_check_create_success') || '新建成功')
            addModalAttrs.confirmLoading = false
            addModalAttrs.visible = false
            getProjectTableData(
              projectTableAttrs.pagination.current,
              props.device.id,
              projectTableAttrs.pagination.pageSize
            )
          } catch (e) {
            addModalAttrs.confirmLoading = false
          }
        },
        cancel() {
          addModalAttrs.visible = false
        },
      }
    )

    const addFormAttrs = reactive({
      labelCol: { span: 5 },
      wrapperCol: { span: 12 },
      selfUpdate: true,
      form: root.$form.createForm(root),
    })
    const validate = () => {
      return new Promise((resolve, reject) => {
        addFormAttrs.form.validateFields((err, values) => {
          if (err) reject(err)
          else resolve(values)
        })
      })
    }

    const projectList = ref([])
    const getProjectList = async () => {
      const res = await cpsp.pageCheckProjects({
        keyWord: '',
        query: {
          current: 1,
          size: 999,
        },
      })
      projectList.value = res.records
    }
    const currentProject = ref({})
    const setCurrentProject = projectId => {
      currentProject.value = projectList.value.find(item => item.id === projectId) || {}
    }

    // created
    getProjectList()

    watch(
      device,
      device => {
        if (!device || !device.id) return
        getProjectTableData(projectTableAttrs.pagination.current, device.id, projectTableAttrs.pagination.pageSize)
      },
      { immediate: true }
    )

    return {
      addButtonProps,
      addButtonEvents,

      projectTableAttrs,
      deleteProject,

      itemTableProps,

      projectList,
      getProjectList,
      currentProject,
      setCurrentProject,

      addModalAttrs,
      addModalEvents,
      addFormAttrs,
    }
  },
})
</script>

<template>
  <div>
    <div>
      <a-button v-acl="'page_business_create_spot_check'" v-bind="addButtonProps" v-on="addButtonEvents">{{
        $t('page_tpm_check_project_create') || '添加点检'
      }}</a-button>
    </div>

    <ml-table v-bind="projectTableAttrs">
      <template #action="record">
        <a-popconfirm
          v-acl="'page_business_delete_spot_check'"
          :title="$t('page_resource_pool_confirm_delete')"
          :ok-text="$t('yes')"
          :cancel-text="$t('no')"
          @confirm="deleteProject(record)"
        >
          <a style="color: var(--color-red)">{{ $t('delete' || '删除') }}</a>
        </a-popconfirm>
      </template>
    </ml-table>
    <ml-table v-bind="itemTableProps"></ml-table>

    <a-modal v-bind="addModalAttrs" v-on="addModalEvents">
      <a-form v-bind="addFormAttrs">
        <a-form-item :label="$t('page_tpm_check_project_name' || '点检项目')">
          <a-select
            :placeholder="$t('please_select') + $t('page_tpm_check_project_name' || '点检项目')"
            v-decorator="[
              'checkProjectId',
              { rules: [{ required: true, message: $t('page_dform_must' || '必填项') }] },
            ]"
            @change="setCurrentProject"
          >
            <a-select-option v-for="item in projectList" :key="item.id" :value="item.id">
              {{ item.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('page_tpm_check_project_interval' || '点检周期')">
          <a-input-number :placeholder="$t('please_select') + $t('page_tpm_check_project_interval' || '点检周期')" v-decorator="['intervalTime']" :min="0" :max="999999999" />
        </a-form-item>
        <a-form-item :label="$t('page_tpm_check_project_remark' || '备注')">
          <a-textarea :placeholder="$t('please_select') + $t('page_tpm_check_project_remark' || '备注')" v-decorator="['remark', { rules: [{ max: 100 }] }]" :maxLength="100" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>
